import React, { useEffect, useState, useReducer } from 'react';
import cn from 'classnames';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import {
  hasCustomDistractorRationale,
  getCustomDistractorRationaleProps,
  injectCustomDistractorRationale,
  convertLearnosityDistractorRationaleNumberToLetter,
  isCustomDistractorRationaleSaved,
} from 'chameleon/legacy/packages/lrn-player/src/modules/Player/utility/Utils';
import { setCustomDistractorsProps } from 'chameleon/legacy/packages/lrn-player/src/modules/Player/state/actions';
import {
  reducer,
  defaultValues,
} from 'chameleon/legacy/packages/lrn-player/src/modules/Player/state/reducer';
import CustomDistractorRationale from './CustomDistractorRationale';

const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    // remove correct answer from screen reader
    if (mutation.target.classList.contains('lrn-accessibility-arialive')) {
      mutation.addedNodes.forEach((node) => {
        node.nodeValue = '';
      });
    }
    // read distractor after being added
    if (
      mutation.addedNodes?.[0]?.classList?.contains(
        'lrn_distractor_rationale_wrapper',
      )
    ) {
      const nodes = mutation.addedNodes[0].querySelectorAll(
        '.lrn_distractor_rationale_content',
      );
      if (nodes.length > 0) {
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          setTimeout(() => {
            node.setAttribute('aria-live', 'polite');
            node.setAttribute('aria-atomic', 'true');
            node.innerHTML += '';
          }, 200);
        }
      }
    }
  });
});

const CheckAnswerButton = ({
  app,
  isImpersonating,
  immediateFeedbackAttempts = 0,
}) => {
  const [remainingAttempts, setRemainingAttempts] = useState(
    immediateFeedbackAttempts,
  );
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [activeQuestionReference, setActiveQuestionReference] = useState('');
  const [hasOptionsSelected, setHasOptionsSelected] = useState(false);
  const [state, dispatch] = useReducer(reducer, defaultValues);

  const updateCurrentQuestion = () => {
    const responseId = app.getCurrentItem().questions[0].response_id;
    const question = app.question(responseId);

    app.question(responseId).on('changed', onQuestionSelection);

    setActiveQuestion((prevQuestions) => {
      // Remove the previous question handler
      const prevResponseId = prevQuestions?.getQuestion().response_id;
      if (prevResponseId) {
        app.question(prevResponseId).off('changed', onQuestionSelection);
      }

      return question;
    });

    setActiveQuestionReference(question?.getMetadata()?.sheet_reference);
    updateRemainingAttempts(question, false);

    return question;
  };

  const updateRemainingAttempts = (question, shouldDecrease) => {
    if (isImpersonating) {
      const responseId = question.getQuestion().response_id;
      const impersonatedAttempts =
        JSON.parse(localStorage.getItem('impersonatedAttempts')) || {};
      const modification = shouldDecrease ? 1 : 0;

      const activeQuestionImpersonatedAttempts =
        (impersonatedAttempts[responseId] || 0) + modification;
      const updatedValue =
        immediateFeedbackAttempts - activeQuestionImpersonatedAttempts;

      impersonatedAttempts[responseId] = activeQuestionImpersonatedAttempts;

      localStorage.setItem(
        'impersonatedAttempts',
        JSON.stringify(impersonatedAttempts),
      );

      setRemainingAttempts(updatedValue);
    } else {
      const feedbackAttemptsCount =
        question.getResponse()?.feedbackAttemptsCount || 0;
      setRemainingAttempts(immediateFeedbackAttempts - feedbackAttemptsCount);
    }
  };

  const onClick = () => {
    activeQuestion.validate({
      showCorrectAnswers: true,
      showDistractorRationale: true,
      feedbackAttempts: !isImpersonating,
    });

    const questionResponse = activeQuestion.getResponse();
    const questionMetadata = activeQuestion.getMetadata();

    const distractorProps = getCustomDistractorRationaleProps(
      activeQuestion,
      questionResponse,
      questionMetadata,
    );
    if (hasCustomDistractorRationale(questionMetadata)) {
      dispatch(
        setCustomDistractorsProps(
          distractorProps,
          questionMetadata.sheet_reference,
        ),
      );
    } else {
      const itemReference = app.getCurrentItem().reference;
      convertLearnosityDistractorRationaleNumberToLetter(
        itemReference,
        distractorProps.selectedOptions,
      );
    }

    if (questionResponse) {
      activeQuestion.response.setMetadata(questionResponse);

      app.save();
    }

    updateRemainingAttempts(activeQuestion, true);
  };

  const updateSelectedQuestions = (responseId) => {
    const selectedOptions = app.question(responseId).getResponse()?.value;
    const hasSelected =
      Array.isArray(selectedOptions) && selectedOptions?.length > 0;
    setHasOptionsSelected(hasSelected);
  };

  const onQuestionSelection = () => {
    const responseId = app.getCurrentItem().questions[0].response_id;
    updateSelectedQuestions(responseId);
  };

  const onQuestionChanged = () => {
    const responseId = app.getCurrentItem().questions[0].response_id;
    updateSelectedQuestions(responseId);
    updateCurrentQuestion();
  };

  useEffect(() => {
    if (
      !activeQuestion ||
      isCustomDistractorRationaleSaved(
        activeQuestionReference,
        state.customDistractorsProps,
      )
    ) {
      return;
    }
    const questionResponse = activeQuestion.getResponse();
    const questionMetadata = activeQuestion.getMetadata();
    const itemReference = app.getCurrentItem().reference;

    const distractorProps = getCustomDistractorRationaleProps(
      activeQuestion,
      questionResponse,
      questionMetadata,
    );

    convertLearnosityDistractorRationaleNumberToLetter(
      itemReference,
      distractorProps.selectedOptions,
    );
  }, [activeQuestion]);

  useEffect(() => {
    const responseId = app.getCurrentItem().questions[0].response_id;

    updateCurrentQuestion();
    updateSelectedQuestions(responseId);

    app.on('item:changed', onQuestionChanged);

    app.question(responseId).on('rendered', () => {
      const lrnContainer = document.querySelector('.Player');
      observer.observe(lrnContainer, { childList: true, subtree: true });
    });

    return () => {
      app.off('item:changed', onQuestionChanged);
      localStorage.removeItem('impersonatedAttempts');
      observer.disconnect();
    };
  }, []);

  if (activeQuestion?.getQuestion().type !== 'mcq') {
    return null;
  }

  const isDisabled =
    (immediateFeedbackAttempts !== 0 && remainingAttempts < 1) ||
    !hasOptionsSelected;
  return (
    <>
      {isCustomDistractorRationaleSaved(
        activeQuestionReference,
        state.customDistractorsProps,
      ) &&
        injectCustomDistractorRationale(
          <CustomDistractorRationale
            {...state?.customDistractorsProps?.[activeQuestionReference]}
          />,
          activeQuestionReference,
        )}
      <ButtonWithIcon
        iconAccessibility={{
          ariaHidden: true,
        }}
        icon="checkmark"
        size="large"
        isLink
        isOutline
        className="mr-4.6"
        containerClassName="flex"
        onClick={onClick}
        disabled={isDisabled}
        aria-live="polite"
      >
        <span aria-live="off">Check Answer</span>
        {immediateFeedbackAttempts !== 0 && (
          <span
            aria-live="off"
            className={cn('rounded-full w-8 h-8 flex justify-center ml-3', {
              'text-blue-600 bg-blue-300':
                remainingAttempts > 0 && hasOptionsSelected,
              'text-gray-600 bg-gray-300': !hasOptionsSelected,
              'text-red-500 bg-red-200':
                remainingAttempts <= 0 && hasOptionsSelected,
            })}
          >
            {remainingAttempts}
          </span>
        )}
      </ButtonWithIcon>
    </>
  );
};

export default CheckAnswerButton;
